import { useParams } from 'react-router-dom';

import {
  selectAnalyticalVariables,
  selectCriteria,
  useCohortDefinitionStore,
} from '@verantos-dev/app-state-cohort-definition';
import {
  useAnalyticalVariableListQuery,
  type AnalyticalVariableListItem,
} from '@verantos-dev/feature-analytical-variables';
import type { AnalyticalVariable } from '@verantos-dev/types-cohorts';
import type { Criterion } from '@verantos-dev/types-cohorts';

export interface CriterionRow {
  /** 1-based serial for display ('01', '02', …) over the filtered card list. */
  index: number;
  /** Stable React key. */
  key: string;
  /**
   * Stable drag-and-drop id — the AV id or library reference id backing
   * this criterion, unaffected by reorders (unlike `key`, which embeds the
   * row's current display index). Only meaningful when `isIndexDate` is
   * false; index-date criteria are never draggable.
   */
  dragId: string;
  /** True for the index-date criterion — pinned first, never draggable. */
  isIndexDate: boolean;
  /** The cohort criterion as it lives in the Zustand store. */
  criterion: Criterion;
  /**
   * AV referenced by the criterion. Three resolution paths:
   *   1. Cohort-local: `criterion.analyticalVariableId` matches an entry in
   *      the store's `analyticalVariables` (legacy clone-on-add path).
   *   2. Library reference (PR #3647): `criterion.analyticalVariableId` is
   *      null and `sourceLibraryAvId` points at a library entry. We fetch
   *      via the library list query and synthesize a thin AV here.
   *   3. Unbound stub or library miss: undefined.
   */
  analyticalVariable: AnalyticalVariable | undefined;
  /**
   * Raw library item when the criterion resolved via reference-by-id. Lets
   * the card branch on `kind` ('av_ir' | 'av_denovo') and surface sql_body
   * without re-fetching downstream.
   */
  libraryItem?: AnalyticalVariableListItem;
}

/**
 * Pure Zustand read for the criteria card list. AV-only — surfaces
 * analytical-variable + index-date criteria; legacy `subject_variable`
 * criteria are filtered out (not part of the v3 cohort model).
 *
 * Resolves criteria added via the reference-by-id model (PR #3647) by
 * looking up `sourceLibraryAvId` against the project's library list. The
 * cohort store no longer carries a local clone of library AVs, so the
 * render path has to reach into the library to fill in name/description/SQL.
 */
export const useCriteriaSection = (): CriterionRow[] => {
  const { projectId } = useParams();
  const criteria = useCohortDefinitionStore(selectCriteria);
  const analyticalVariables = useCohortDefinitionStore(selectAnalyticalVariables);
  // List query is already cached project-wide by the pickers — this is a
  // free read in most cases. The card never falls back to a per-item detail
  // fetch; library AVs always come through the list.
  const { data: libraryResponse } = useAnalyticalVariableListQuery(undefined, projectId);
  const libraryItems = libraryResponse?.data ?? [];

  const avById = new Map(analyticalVariables.map((av) => [av.id, av]));
  const libraryById = new Map(libraryItems.map((item) => [item.analyticalVariableId, item]));

  return criteria
    .filter(
      (criterion) =>
        criterion.criteriaType === 'analytical_variable' || criterion.criteriaType === 'index_date'
    )
    .sort((a, b) => {
      if (a.criteriaType === 'index_date') return -1;
      if (b.criteriaType === 'index_date') return 1;
      return 0;
    })
    .map((criterion, idx): CriterionRow => {
      let avId: string | null | undefined;
      let sourceLibraryAvId: string | null | undefined;
      if (criterion.criteriaType === 'analytical_variable') {
        avId = criterion.analyticalVariableId;
        sourceLibraryAvId = criterion.sourceLibraryAvId;
      } else if (
        criterion.criteriaType === 'index_date' &&
        criterion.value.type === 'Analytical variable'
      ) {
        avId = criterion.value.analyticalVariableId;
        sourceLibraryAvId = criterion.value.sourceLibraryAvId;
      }

      // Resolution priority: local AV first (legacy clone), then library
      // ref (current model). Falls through to undefined for unbound stubs.
      const localAv = avId ? avById.get(avId) : undefined;
      const libraryItem = sourceLibraryAvId ? libraryById.get(sourceLibraryAvId) : undefined;
      const resolvedAv =
        localAv ??
        (libraryItem?.jsonValue
          ? // av_ir from library: surface the structured tree directly.
            libraryItem.jsonValue
          : libraryItem
          ? // av_denovo from library: synthesize a minimal AV-shaped
            // record so the card has name/description without crashing
            // on missing structured fields. The card branches on
            // `libraryItem.kind` for SQL-aware rendering.
            ({
              id: libraryItem.analyticalVariableId,
              name: libraryItem.name,
              description: libraryItem.description,
              logic: null,
            } as unknown as AnalyticalVariable)
          : undefined);

      return {
        index: idx + 1,
        key: `${idx}-${sourceLibraryAvId ?? avId ?? criterion.criteriaType}`,
        dragId: sourceLibraryAvId ?? avId ?? `${idx}-${criterion.criteriaType}`,
        isIndexDate: criterion.criteriaType === 'index_date',
        criterion,
        analyticalVariable: resolvedAv,
        libraryItem,
      };
    });
};
